import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useEducation } from "../../../hooks/useEducation";
import "./ClassCard.css";
import filledheart from "../../../assets/new-education/class_filled_heart.svg";
import unfilledheart from "../../../assets/new-education/class_unfilled_heart.svg";
import ClassDetailModal from "./ClassDetailModal";

const ClassCard = ({ classItem, courseId, enrollmentId, is_course }) => {
  const navigate = useNavigate();
  const [classProgress, setClassProgress] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [classContents, setClassContents] = useState([]);
  const [contentsLoading, setContentsLoading] = useState(false);

  const {
    fetchClassProgress,
    createClassProgress,
    toggleFavoriteClass,
    toggleFavoriteCourse,
    getContentsByClass, // Make sure this hook method exists
  } = useEducation();

  useEffect(() => {
    const getClassProgress = async () => {
      setIsLoading(true);
      try {
        const data = await fetchClassProgress({
          my_course: enrollmentId,
          edu_class: classItem.id,
        });

        if (data && data.length > 0) {
          setClassProgress(data[0]);
        }
      } catch (error) {
        console.error("Error fetching class progress:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (enrollmentId && classItem.id && !is_course) {
      getClassProgress();
    }
  }, [enrollmentId, classItem.id, fetchClassProgress, is_course]);

  const fetchClassContents = async () => {
    if (!classItem.id) return;

    setContentsLoading(true);
    try {
      const contents = await getContentsByClass(classItem.id);
      setClassContents(contents || []);
    } catch (error) {
      console.error("Error fetching class contents:", error);
      setClassContents([]);
    } finally {
      setContentsLoading(false);
    }
  };

  const handleClassClick = async () => {
    if (is_course) {
      // Handle course click if needed
      return;
    }

    // Fetch class contents when opening modal
    await fetchClassContents();

    // Show modal instead of navigating directly
    setShowModal(true);
  };

  const handleGetStarted = async () => {
    if (classProgress) {
      // If we already have class progress, navigate to class page
      navigate(`/education/class/${classItem.id}`, {
        state: {
          classProgress,
          courseId,
          enrollmentId,
        },
      });
    } else {
      // If no class progress exists, we'll start the class
      await handleStartClass();
    }
  };

  const handleStartClass = async () => {
    try {
      // Create class progress for this class using the hook
      const newClassProgress = await createClassProgress({
        my_course: enrollmentId,
        edu_class: classItem.id,
        status: "not_started",
      });

      // Navigate to class page with the newly created progress
      navigate(`/education/class/${classItem.id}`, {
        state: {
          classProgress: newClassProgress,
          courseId,
          enrollmentId,
        },
      });
    } catch (error) {
      console.error("Error starting class:", error);
    }
  };

  const handleFavoriteToggle = async (e) => {
    e.stopPropagation(); // Prevent triggering the card click

    try {
      if (is_course) {
        // Handle course favorite toggle
        await toggleFavoriteCourse(enrollmentId);
      } else {
        // Handle class favorite toggle
        let progressId;

        if (classProgress) {
          // Use existing class progress
          progressId = classProgress.id;
        } else {
          // Create class progress first if it doesn't exist
          const newClassProgress = await createClassProgress({
            my_course: enrollmentId,
            edu_class: classItem.id,
            status: "not_started",
          });
          setClassProgress(newClassProgress);
          progressId = newClassProgress.id;
        }

        const updatedProgress = await toggleFavoriteClass(progressId);
        setClassProgress((prev) => ({
          ...prev,
          is_favorite: updatedProgress.is_favorite,
        }));
      }
    } catch (error) {
      console.error("Error toggling favorite:", error);
    }
  };

  return (
    <>
      <div
        className="class-card"
        onClick={handleClassClick}
        style={{
          background: is_course
            ? "linear-gradient(180deg, #C4A68C 0%, #5D3221 100%)"
            : "#C4A68C",
          opacity: 0.8,
        }}
      >
        <div className="heart-icon-container" onClick={handleFavoriteToggle}>
          <i
            className={`heart-icon ${
              classProgress?.is_favorite ? "filled" : ""
            }`}
          >
            {classProgress?.is_favorite || classItem?.is_favorite ? (
              <img
                src={filledheart}
                alt="Filled heart"
                width={20}
                height={20}
              />
            ) : (
              <img
                src={unfilledheart}
                alt="Unfilled heart"
                width={20}
                height={20}
              />
            )}
          </i>
        </div>

        <div className="class-content">
          <h3 className="class-title">
            {classItem.name ? classItem.name : classItem.course_name}
          </h3>
        </div>
      </div>

      {/* Class Detail Modal */}
      <ClassDetailModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        classItem={classItem}
        contents={classContents}
        contentsLoading={contentsLoading}
        onGetStarted={handleGetStarted}
      />
    </>
  );
};

export default ClassCard;
